// WO-33 — Piece 4: the "Show me around" tour (spec §5). Four steps, only when
// the user asks (welcome button or help menu). It opens the Tenders list, then
// highlights one element per step. If an element is not on the page (layout,
// phone, empty list) the bubble shows centred with no highlight. It never
// throws: every DOM read is guarded, and the whole tour sits inside an
// OnboardingBoundary in the layout.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useOnboarding } from './OnboardingContext';
import { onboardingCopy } from './copy';
import { btnPrimary, btnSecondary, panel, fontStyle } from './styles';

function visible(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function firstVisible(selectors) {
  for (const sel of selectors) {
    try {
      const all = document.querySelectorAll(sel);
      for (const el of all) if (visible(el)) return el;
    } catch { /* bad selector: skip */ }
  }
  return null;
}

// Step targets, in order of preference.
const TARGETS = [
  ['aside nav', '[data-onb="menu-button"]'],
  ['[data-onb="tender-filters"]'],
  ['[data-onb="card-actions"]'],
  ['[data-onb="help-button"]'],
];

const WAIT_MS = 2500;
const PAD = 6;

export default function Tour() {
  const onb = useOnboarding();
  const c = onboardingCopy.en.tour;
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  // The element found for a step, tagged with that step, so a measurement can
  // never use the previous step's element.
  const [found, setFound] = useState({ step: -1, el: null });
  const ready = found.step === step;
  const bubbleRef = useRef(null);
  const n = c.steps.length;

  const close = useCallback(() => { setStep(0); setFound({ step: -1, el: null }); onb.endTour(); }, [onb]);

  // Find the element for this step, waiting briefly for the page to render.
  useEffect(() => {
    if (!onb.tourActive) return undefined;
    let cancelled = false;
    const started = Date.now();
    const look = () => {
      if (cancelled) return;
      const el = firstVisible(TARGETS[step] || []);
      if (el || Date.now() - started > WAIT_MS) {
        if (el) {
          try { el.scrollIntoView({ block: 'center', inline: 'nearest' }); } catch { /* old browsers */ }
        }
        setFound({ step, el });
        return;
      }
      setTimeout(look, 150);
    };
    look();
    return () => { cancelled = true; };
  }, [onb.tourActive, step]);

  // Keep the highlight on the element while the page scrolls or resizes.
  useLayoutEffect(() => {
    if (!onb.tourActive || !ready) return undefined;
    const measure = () => {
      const el = found.el;
      if (el && visible(el)) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
      } else {
        setRect(null);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [onb.tourActive, ready, found]);

  // Focus the bubble for keyboard users; Esc closes.
  useEffect(() => {
    if (!onb.tourActive || !ready) return undefined;
    try { bubbleRef.current && bubbleRef.current.focus(); } catch { /* ignore */ }
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
      if (e.key === 'ArrowRight' && step < n - 1) setStep((s) => s + 1);
      if (e.key === 'ArrowLeft' && step > 0) setStep((s) => s - 1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onb.tourActive, ready, step, n, close]);

  if (!onb.tourActive || !ready) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const bw = Math.min(320, vw - 32);
  let bubbleStyle;
  if (rect) {
    const below = rect.top + rect.height + 12;
    const fitsBelow = below + 180 < vh;
    let left = rect.left;
    if (left + bw > vw - 16) left = vw - 16 - bw;
    if (left < 16) left = 16;
    // A tall target (the side menu) gets the bubble beside it.
    if (rect.height > vh * 0.5 && rect.left + rect.width + bw + 24 < vw) {
      bubbleStyle = { top: Math.max(16, Math.min(rect.top + 24, vh - 220)), left: rect.left + rect.width + 12, width: bw };
    } else if (fitsBelow) {
      bubbleStyle = { top: below, left, width: bw };
    } else {
      bubbleStyle = { top: Math.max(16, rect.top - 12 - 180), left, width: bw };
    }
  } else {
    bubbleStyle = { top: '50%', left: '50%', width: bw, transform: 'translate(-50%, -50%)' };
  }

  const last = step === n - 1;

  return (
    <div className="fixed inset-0 z-[60]" dir="ltr" lang="en" style={fontStyle} data-testid="onboarding-tour">
      {rect ? (
        <div
          aria-hidden="true"
          className="fixed rounded-lg ring-2 ring-[#217922] dark:ring-[#3BB34A] pointer-events-none transition-all duration-150"
          style={{ ...rect, boxShadow: '0 0 0 9999px rgba(15,45,78,0.45)' }}
          data-testid="onboarding-tour-highlight"
        />
      ) : (
        <div aria-hidden="true" className="fixed inset-0 bg-[rgba(15,45,78,0.45)]" />
      )}
      <div
        ref={bubbleRef}
        role="dialog"
        aria-modal="true"
        aria-label={c.counter(step + 1, n)}
        tabIndex={-1}
        className={`${panel} fixed p-4 focus:outline-none`}
        style={bubbleStyle}
        data-testid="onboarding-tour-bubble"
        data-step={step + 1}
        data-centred={rect ? 'false' : 'true'}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm text-[#0F2D4E] dark:text-white">{c.steps[step]}</p>
          <button
            type="button"
            onClick={close}
            aria-label={c.close}
            className="rounded-md p-1 -mt-1 -mr-1 text-[#4A5568] dark:text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#217922]"
            data-testid="onboarding-tour-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-[#4A5568] dark:text-muted-foreground mr-auto" data-testid="onboarding-tour-counter">
            {c.counter(step + 1, n)}
          </span>
          {step > 0 && (
            <button type="button" className={btnSecondary} onClick={() => setStep((s) => s - 1)} data-testid="onboarding-tour-back">
              {c.back}
            </button>
          )}
          <button
            type="button"
            className={btnPrimary}
            onClick={() => (last ? close() : setStep((s) => s + 1))}
            data-testid="onboarding-tour-next"
          >
            {last ? c.done : c.next}
          </button>
        </div>
      </div>
    </div>
  );
}
